import { AnyAction, createAsyncThunk } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { PUBLIC_API } from "redux/api"
import {
  AUTHENTICATION_POLLING_INTERVAL,
  AUTHENTICATION_POLLING_TIMEOUT,
  SMART_ID_ERRORS,
} from "config"
import PollingService from "redux/api/pollingService"
import { login } from "redux/features/auth/authSlice"
import {
  start as startVerification,
  reset as resetVerification,
} from "redux/features/verification/verificationSlice"
import { start as startPolling } from "redux/features/polling/pollingSlice"
import { AuthSessionValues, IdCardValues } from "types"

export const fetchIdCardNonce = createAsyncThunk(
  "id-card-nonce/fetch",
  async () => {
    return await PUBLIC_API.get("/authentication/id_card/generate/nonce")
      .then((result) => {
        return result.data
      })
      .catch((error) => {
        return error.response.data
      })
  },
)

export const createIdCardSession = createAsyncThunk(
  "id-card-session/create",
  async (values: IdCardValues) => {
    const response = await PUBLIC_API.post("/authentication/id_card", values)

    return response.data
  },
)

export const fetchIdCardSession = createAsyncThunk(
  "id-card-session/fetch",
  async (values: AuthSessionValues) => {
    return await PUBLIC_API.get(`/authentication/id_card/${values.session_id}`)
      .then((result) => {
        return result.data
      })
      .catch((error) => {
        return error.response.data
      })
  },
)

export const makeIdCardAuthentication = (values: IdCardValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve, reject) => {
      return dispatch(createIdCardSession(values))
        .then(
          ({ payload }) => {
            if (!payload) {
              return reject(i18n.t("notice.sessions.smart_id.unavailable"))
            }

            const { session_id } = values
            // NOTE: make a fake verification to block an interface
            dispatch(startVerification({ code: "0000" }))

            const timerId = setInterval(async () => {
              const pollingId = PollingService.getPolling()

              if (!pollingId) {
                clearInterval(timerId)
                return reject(i18n.t("notice.sessions.smart_id.canceled"))
              }

              const result = await dispatch(fetchIdCardSession({ session_id }))
              const { payload } = result

              if (payload?.authentication) {
                clearInterval(timerId)
                PollingService.resetPolling()

                const { authentication } = result.payload
                const { id, access_token, refresh_token } = authentication

                dispatch(
                  login({
                    id: id,
                    accessToken: access_token,
                    refreshToken: refresh_token,
                  }),
                )
                dispatch(resetVerification())

                resolve(authentication)
              } else if (
                payload?.error &&
                SMART_ID_ERRORS.includes(payload.error)
              ) {
                clearInterval(timerId)
                PollingService.resetPolling()
                dispatch(resetVerification())

                reject(
                  i18n.t(
                    `notice.sessions.smart_id.${payload.error.toLowerCase()}`,
                  ),
                )
              }
            }, AUTHENTICATION_POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout(() => {
              clearInterval(timerId)
              PollingService.resetPolling()
            }, AUTHENTICATION_POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.sessions.smart_id.not_found"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
